<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="smAndDown ? undefined : 760"
    :fullscreen="smAndDown"
    :transition="smAndDown ? 'dialog-bottom-transition' : 'dialog-transition'"
    :content-class="
      smAndDown
        ? 'create-password-dialog create-password-dialog--mobile'
        : 'create-password-dialog'
    "
    @update:model-value="onDialogToggle"
  >
    <div class="create-password">
      <button class="close-btn" type="button" @click="closeDialog">
        <v-icon icon="mdi-close" />
      </button>

      <img
        class="brand-logo"
        src="/img/logo-f-123x190.webp"
        alt="FlixLatino"
      />

      <h2 class="title">{{ $t("avodDialogs.createPassword.title") }}</h2>

      <form class="form" @submit.prevent="onContinue">
        <input
          v-model.trim="localEmail"
          class="field"
          type="email"
          :placeholder="$t('avodDialogs.createPassword.emailPlaceholder')"
          autocomplete="email"
        />

        <input
          v-model="password"
          class="field"
          type="password"
          :placeholder="$t('avodDialogs.createPassword.passwordPlaceholder')"
          autocomplete="new-password"
        />

        <button class="cta-btn" type="submit">{{ $t("avodDialogs.createPassword.continue") }}</button>
      </form>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useDisplay } from "vuetify";

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    email?: string;
  }>(),
  {
    modelValue: false,
    email: "",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "close"): void;
  (e: "continue", payload: { email: string; password: string }): void;
}>();

const { smAndDown } = useDisplay();
const localEmail = ref("");
const password = ref("");

watch(
  () => props.email,
  (value) => {
    localEmail.value = String(value || "");
  },
  { immediate: true }
);

function onDialogToggle(value: boolean) {
  emit("update:modelValue", value);
  if (!value) emit("close");
}

function closeDialog() {
  emit("update:modelValue", false);
  emit("close");
}

function onContinue() {
  emit("continue", {
    email: String(localEmail.value || "").trim(),
    password: String(password.value || ""),
  });
}
</script>

<style scoped>
.create-password {
  position: relative;
  width: 760px;
  min-height: 600px;
  border-radius: 48px;
  border: 1px solid transparent;
  background: radial-gradient(
      75% 80% at 30% 20%,
      rgba(57, 66, 118, 0.6) 0%,
      rgba(31, 39, 81, 0.38) 38%,
      rgba(16, 24, 57, 0.9) 100%
    ),
    linear-gradient(180deg, #1c2650 0%, #11183b 100%);
  padding: 64px 56px 52px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  overflow: hidden;
  box-sizing: border-box;
}

.create-password::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 3rem;
  padding: 2px;
  background: linear-gradient(
    135deg,
    rgba(158, 120, 255, 0.95) 0%,
    rgba(119, 86, 255, 0.9) 35%,
    rgba(255, 64, 153, 0.9) 70%,
    rgba(255, 64, 153, 0.95) 100%
  );
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  color: #f2f5ff;
}

.brand-logo {
  width: 82px;
  height: auto;
  margin-bottom: 28px;
}

.title {
  margin: 0 0 30px 0;
  font-size: 2rem;
  line-height: 120%;
  font-weight: 800;
  letter-spacing: 0;
}

.form {
  width: 100%;
  max-width: 360px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
}

.field {
  width: 100%;
  height: 56px;
  border-radius: 14px;
  border: 1px solid rgba(188, 196, 221, 0.54);
  background: rgba(17, 24, 59, 0.45);
  color: #f4f7ff;
  font-size: 1rem;
  padding: 0 18px;
  outline: none;
  text-align: start;
  box-sizing: border-box;
}

.field::placeholder {
  color: rgba(214, 220, 241, 0.92);
}

.field:focus {
  border-color: rgba(243, 246, 255, 0.85);
}

.cta-btn {
  margin-top: 8px;
  height: 56px;
  border: none;
  border-radius: 14px;
  background: #fa002b;
  color: #fff;
  font-size: 1rem;
  font-weight: 900;
  width: 100%;
}

@media (max-width: 959px) {
  :deep(.create-password-dialog--mobile) {
    margin: 0;
    width: 100vw !important;
    max-width: 100vw !important;
    max-height: 100vh !important;
    height: 100vh !important;
  }

  .create-password {
    border-radius: 0;
    min-height: 100vh;
    width: 100%;
    padding: 42px 20px 30px;
  }

  .create-password::before {
    border-radius: 0;
  }

  .close-btn {
    top: 12px;
    right: 12px;
  }

  .brand-logo {
    width: 48px;
    margin-bottom: 18px;
  }

  .title {
    font-size: 30px;
    margin-bottom: 24px;
  }

  .form {
    max-width: 320px;
    gap: 14px;
  }

  .field,
  .cta-btn {
    height: 54px;
    border-radius: 16px;
  }
}
</style>
